import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import {
  LayoutItem,
  ThreeDPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDModelJoints } from "@/shared/state/visualization/actions";

import {
  useEphemeralWorkspaceState,
  useWorkspaceTimer,
} from "../../WorkspaceCtx";
import { selectAllWorkspaceTopicTrees } from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import {
  BabylonManager,
  type DefaultBindingResolver,
  type ToolMode,
} from "./BabylonManager";
import { useBabylonTheme } from "./BabylonManager/useBabylonTheme";
import { ClearMeasurementsButton } from "./ClearMeasurementsButton";
import { DataServerManager } from "./DataServer/DataServerManager";
import { FreeCamHintAlert } from "./FreeCamHintAlert";
import { threeDLoadingState } from "./loadingState";
import { MeasureToggleButton } from "./MeasureToggleButton";
import { ModelLoader, resolveDefaultBindings } from "./ModelLoader";
import { makeFirstMessageReader } from "./ModelLoader/firstMessageReader";
import {
  ModelLoadErrorAlert,
  type ModelLoadFailure,
} from "./ModelLoadErrorAlert";
import { ModeToggleButton } from "./ModeToggleButton";
import { RecenterButton } from "./RecenterButton";
import styles from "./ThreeDPanel.module.css";
import { ToolOverlays, type ToolOverlaySink } from "./ToolOverlays";

interface ThreeDPanelProps {
  layout: LayoutItem;
  state: ThreeDPanelState;
}

export function ThreeDPanel({ layout, state }: ThreeDPanelProps) {
  const theme = useBabylonTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>();
  const [isSceneLoading, setIsSceneLoading] = React.useState(true);
  const [isDataLoading, setIsDataLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [manager, setManager] = React.useState<BabylonManager | null>(null);
  const [modelFailures, setModelFailures] = React.useState<ModelLoadFailure[]>(
    [],
  );
  const [toolMode, setToolMode] = React.useState<ToolMode>("none");
  // Per-frame tool reports bypass panel state and go straight to the
  // overlay leaf through this ref (see `ToolOverlays`). The panel keeps
  // only the bit its header needs: whether a clear would remove anything.
  // Re-setting that boolean to its current value does not re-render, so it
  // is safe to push every frame.
  const toolOverlaySink = React.useRef<ToolOverlaySink | null>(null);
  const [hasMeasureContent, setHasMeasureContent] = React.useState(false);
  const timer = useWorkspaceTimer();
  const initTheme = React.useRef(theme);
  const initState = React.useRef(state);

  const dispatch = useVizDispatch();
  const services = useDomainServices();
  const { files } = services;
  // One loader per file service, not per render: the manager captures it in
  // its context for its whole lifetime.
  const modelLoader = React.useMemo(() => new ModelLoader(files), [files]);
  const initModelLoader = React.useRef(modelLoader);

  // A model's declared default joint bindings are resolved against the topics
  // the workspace has open when the model loads, which can be long after the
  // manager is built: a file opened later adds topics. So the resolver reads
  // them through a ref rather than capturing the set it was built with.
  const topicTrees = selectAllWorkspaceTopicTrees(useEphemeralWorkspaceState());
  const topicTreesRef = React.useRef(topicTrees);
  React.useEffect(
    function trackOpenTopics() {
      topicTreesRef.current = topicTrees;
    },
    [topicTrees],
  );
  const readFirstMessage = React.useMemo(
    () => makeFirstMessageReader(services),
    [services],
  );
  // Captured once, like the model loader: the manager keeps it in its context
  // for its whole lifetime.
  const initResolveDefaultBindings = React.useRef<DefaultBindingResolver>(
    (joints, signal) =>
      resolveDefaultBindings(
        joints,
        topicTreesRef.current,
        readFirstMessage,
        signal,
      ),
  );

  const initManager = React.useCallback(
    (
      dataServerManager: DataServerManager,
      time: bigint,
      canvas: HTMLCanvasElement,
    ) => {
      try {
        const manager = new BabylonManager({
          dataServer: dataServerManager,
          modelLoader: initModelLoader.current,
          resolveDefaultBindings: initResolveDefaultBindings.current,
          canvas,
          time,
          initialState: initState.current,
          theme: initTheme.current,
          callbacks: {
            onSceneLoadingChanged: (sceneLoading) => {
              setIsSceneLoading(sceneLoading);
            },
            onDataLoadingChanged: (dataLoading) => {
              setIsDataLoading(dataLoading);
            },
            onModelJointsRead: (entityId, joints) => {
              // Picking a model commits only its file, so this is where the
              // joints the file declares reach the panel state, for the
              // settings to list and a saved workspace to keep.
              dispatch(
                setThreeDModelJoints(initState.current.id, {
                  entityId,
                  ...joints,
                }),
              );
            },
            onModelLoadFailed: (modelId, modelError) => {
              // Replace any earlier failure for the same model so a
              // reconfigured-then-failed model is reported once, not twice.
              setModelFailures((failures) => [
                ...failures.filter((failure) => failure.modelId !== modelId),
                { modelId, message: modelError.message },
              ]);
            },
            onToolHover: (hover) => {
              toolOverlaySink.current?.onToolHover(hover);
            },
            onMeasureOverlay: (overlay) => {
              setHasMeasureContent(overlay !== null);
              toolOverlaySink.current?.onMeasureOverlay(overlay);
            },
          },
        });
        setManager(manager);
        return manager;
      } catch (e) {
        // This is normally a WebGL Not Supported error, which is unrecoverable.
        ErrorMonitoringService.captureError(e);
        setError(e as Error);
        // Short-circuit because the panel is not viable.
        return;
      }
    },
    [
      dispatch,
      setIsSceneLoading,
      setIsDataLoading,
      setManager,
      setError,
      setModelFailures,
      setHasMeasureContent,
    ],
  );

  React.useEffect(
    function onStateUpdated() {
      try {
        manager?.setState(state);
      } catch (e) {
        ErrorMonitoringService.captureError(e);
      }
    },
    [state, manager],
  );

  // Serialized rather than a Set so the effect below has a stable dependency.
  // JSON rather than a joined string because a node's id embeds a file path,
  // which may contain any delimiter one might pick.
  const drawnModelNodeIds = React.useMemo(
    () =>
      JSON.stringify(
        Object.values(state.data.entities)
          .flatMap((entity) => {
            const mesh = entity.mesh;
            return mesh?.type === "model_node"
              ? [
                  `${mesh.file.relativePath ?? mesh.file.fileId}#${mesh.nodeName}`,
                ]
              : [];
          })
          .sort(),
      ),
    [state.data.entities],
  );
  React.useEffect(
    function forgetFailuresForRemovedNodes() {
      // A node whose entity was deleted — or whose mesh was repointed, which
      // restarts its load — should not keep showing its old error. Failures for
      // nodes still being drawn survive until they reload.
      const stillDrawn = new Set(JSON.parse(drawnModelNodeIds) as string[]);
      setModelFailures((failures) =>
        failures.every((failure) => stillDrawn.has(failure.modelId))
          ? failures
          : failures.filter((failure) => stillDrawn.has(failure.modelId)),
      );
    },
    [drawnModelNodeIds],
  );

  React.useEffect(
    function updateTheme() {
      manager?.setTheme(theme);
    },
    [theme, manager],
  );

  React.useEffect(
    function updateToolMode() {
      manager?.setToolMode(toolMode);
    },
    [toolMode, manager],
  );

  const removeMeasurement = React.useCallback(
    (id: number) => {
      manager?.removeMeasurement(id);
    },
    [manager],
  );

  const [dataClient, setDataClient] = React.useState<DataServerManager | null>(
    null,
  );
  React.useEffect(
    function initDataServer() {
      const controller = new AbortController();
      DataServerManager.create(controller)
        .then((dsm) => {
          setDataClient(dsm);
        })
        .catch((e) => {
          if (!controller.signal.aborted && !isAbortError(e)) {
            ErrorMonitoringService.captureError(e);
          }
        });
      return () => {
        controller.abort();
      };
    },
    [setDataClient],
  );

  React.useEffect(
    function init() {
      if (canvasRef.current === undefined || dataClient === null) {
        return;
      }
      const canvas = canvasRef.current;
      // `ToolOverlays` publishes the sink from a layout effect, so it is set
      // before this effect runs and outlives every manager the panel builds.
      const overlays = toolOverlaySink.current;

      // Absorb canvas mouse scrolls.
      function absorbMouseWheelEvents(event: MouseEvent) {
        event.preventDefault();
        return false;
      }

      canvas.addEventListener("wheel", absorbMouseWheelEvents, false);
      canvas.addEventListener("drag", absorbMouseWheelEvents, false);
      canvas.addEventListener("click", absorbMouseWheelEvents, false);

      const manager = initManager(dataClient, timer.currentTime, canvas);
      const tickListener = manager
        ? timer.addListener("tick", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      const seekListener = manager
        ? timer.addListener("seek", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      return () => {
        setManager(null);
        setDataClient(null);
        // These describe the manager disposed below, and nothing else clears
        // them: a replacement manager starts with nothing reported, so it
        // never fires a clearing callback. Left set, the old scene's tooltip,
        // overlay and header spinner would stay on screen indefinitely.
        overlays?.onToolHover(null);
        overlays?.onMeasureOverlay(null);
        setHasMeasureContent(false);
        setIsDataLoading(false);
        manager?.dispose();

        canvas.removeEventListener("wheel", absorbMouseWheelEvents);
        canvas.removeEventListener("drag", absorbMouseWheelEvents);
        canvas.removeEventListener("click", absorbMouseWheelEvents);
        if (tickListener) {
          timer.removeListener(tickListener);
        }
        if (seekListener) {
          timer.removeListener(seekListener);
        }
      };
    },
    [dataClient, initManager, setManager, timer],
  );

  const loadingState = threeDLoadingState({
    sceneLoading: isSceneLoading,
    dataLoading: isDataLoading,
    failed: error !== null,
  });

  return (
    <PanelLayout
      header={
        <PanelHeader
          backgroundLoading={loadingState === "background"}
          additionalTools={
            <>
              <MeasureToggleButton
                mode={toolMode}
                onChange={setToolMode}
                disabled={manager === null}
              />
              {/*
                A non-null overlay means the measure state holds a completed
                measurement or an anchor, the same condition under which
                `clearMeasurements` does anything. Gating on label count
                instead would strand a lone anchor's dot on screen with no
                control to remove it.
              */}
              <ClearMeasurementsButton
                manager={manager}
                canClear={hasMeasureContent}
              />
              <RecenterButton manager={manager} state={state} />
              <ModeToggleButton
                panelId={state.id}
                manager={manager}
                state={state}
              />
            </>
          }
          className={styles.panelHeader}
          state={state}
        />
      }
      isLoading={loadingState === "foreground"}
      layout={layout}
      state={state}
    >
      {!error && (
        <canvas
          className={styles.canvas}
          ref={(ref) => (canvasRef.current = ref ?? undefined)}
        />
      )}
      <ToolOverlays
        sink={toolOverlaySink}
        toolMode={toolMode}
        onRemoveMeasurement={removeMeasurement}
      />
      <FreeCamHintAlert
        cameraType={state.config?.camera?.type}
        className={styles.freeCamHint}
      />
      <ModelLoadErrorAlert
        failures={modelFailures}
        onDismiss={() => setModelFailures([])}
        className={styles.modelLoadError}
      />
      {/* TODO(dave): enable onClearError in cases where the error is recoverable. */}
      {error && <RenderingError error={error} />}
      <DataNotFoundMessage panelId={state.id} />
    </PanelLayout>
  );
}
